import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { showToast } from '../../lib/toast';
import { useOnboardingStore } from '../../store/useOnboardingStore';

// Step components imports
import { CompanyOverview } from './steps/CompanyOverview';
import { TeamSize } from './steps/TeamSize';
import { CompanyAddress } from './steps/CompanyAddress';
import { UserDetails } from './steps/UserDetails';
import { InviteTeam } from './steps/InviteTeam';
import { Confirmation } from './steps/Confirmation';

interface FormData {
  companyName: string;
  industry: string;
  website: string;
  teamSize: string;
  country: string;
  state: string;
  city: string;
  phone: string;
  companyEmail: string;
  firstName: string;
  lastName: string;
  role: string;
  invites: string[];
}

const steps = [
  {
    id: 1,
    title: 'Company Overview',
    description: 'Basic information about your company'
  },
  {
    id: 2,
    title: 'Team Size',
    description: 'Number of employees in your organization'
  },
  {
    id: 3,
    title: 'Company Address',
    description: 'Your company location and contact details'
  },
  {
    id: 4,
    title: 'User Details',
    description: 'Information about the account owner'
  },
  {
    id: 5,
    title: 'Invite Team',
    description: 'Add your team members'
  },
  {
    id: 6,
    title: 'Confirmation',
    description: 'Review and complete setup'
  }
];

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const { closeModal } = useOnboardingStore();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    industry: '',
    website: '',
    teamSize: '1-10',
    country: '',
    state: '',
    city: '',
    phone: '',
    companyEmail: '',
    firstName: '',
    lastName: '',
    role: '',
    invites: [''],
  });

  const isStepValid = (): boolean => {
    switch (currentStep) {
      case 1:
        return formData.companyName.trim() !== '' && formData.industry.trim() !== '';
      case 2:
        return formData.teamSize.trim() !== '';
      case 3:
        return formData.country.trim() !== '' && formData.state.trim() !== '';
      case 4:
        return formData.firstName.trim() !== '' && formData.lastName.trim() !== '';
      case 5:
        return true; // Optional step
      case 6:
        return true; // Confirmation step
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length && isStepValid()) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = async () => {
    try {
      setIsSubmitting(true);
      await saveDataToSupabase();
      closeModal();
      navigate('/dashboard');
    } catch (error) {
      showToast.error('Failed to save data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveDataToSupabase = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // First, create the user record
      const { data: userData, error: userInsertError } = await supabase
        .from('users')
        .insert({
          id: user?.id,
          email: user?.email,
          role: formData.role || 'employee',
          first_name: formData.firstName,
          last_name: formData.lastName
        })
        .select()
        .single();

      if (userInsertError) throw userInsertError;

      // Then, create the company record
      const { error: companyError } = await supabase
        .from('companies')
        .insert({
          user_id: userData.id,
          company_name: formData.companyName,
          industry: formData.industry,
          website: formData.website,
          country: formData.country,
          state: formData.state,
          city: formData.city,
          contact_email: formData.companyEmail,
          contact_phone: formData.phone,
          team_size: formData.teamSize
        });

      if (companyError) throw companyError;

      showToast.success('Setup completed successfully!');
    } catch (error: any) {
      console.error('Error saving data:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await saveDataToSupabase();
      closeModal();
      navigate('/dashboard');
    } catch (error) {
      showToast.error('Failed to save data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <CompanyOverview data={formData} updateData={updateFormData} />;
      case 2:
        return <TeamSize data={formData} updateData={updateFormData} />;
      case 3:
        return <CompanyAddress data={formData} updateData={updateFormData} />;
      case 4:
        return <UserDetails data={formData} updateData={updateFormData} />;
      case 5:
        return <InviteTeam data={formData} updateData={updateFormData} onSkip={handleSkip} />;
      case 6:
        return <Confirmation onSubmit={handleSubmit} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-[1000px] h-[600px] flex overflow-hidden bg-white rounded-2xl">
      {/* Left Sidebar - Steps */}
      <div className="w-[300px] bg-gray-50 p-8 border-r border-gray-200">
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900">Setup Guide</h2>
          <p className="text-sm text-gray-500 mt-1">Complete all steps to get started</p>
        </div>

        <div className="space-y-6">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-start gap-4 ${
                currentStep === step.id ? 'opacity-100' : 'opacity-60'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  currentStep === step.id
                    ? 'bg-[#4B6BFB] text-white'
                    : currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step.id ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="text-sm">{step.id}</span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">{step.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-8">
          {renderStep()}
        </div>

        {/* Navigation Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            onClick={handleBack}
            className={`px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors ${
              currentStep === 1 ? 'invisible' : ''
            }`}
            disabled={isSubmitting}
          >
            Back
          </button>

          <div className="flex gap-3">
            {currentStep === 5 && (
              <button
                onClick={handleSkip}
                className="px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Skip'}
              </button>
            )}
            <button
              onClick={currentStep === steps.length ? handleSubmit : handleNext}
              disabled={!isStepValid() || isSubmitting}
              className={`px-6 py-2 rounded-lg ${
                isStepValid() && !isSubmitting
                  ? 'bg-[#4B6BFB] text-white hover:bg-[#4B6BFB]/90'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting
                ? 'Saving...'
                : currentStep === steps.length
                ? 'Complete Setup'
                : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};